import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, 
  Search, 
  Plus, 
  Download,
  Edit,
  Trash2,
  Mail,
  Calendar,
  User,
  Clock
} from 'lucide-react';
import { generateRemindersPdf } from '../utils/pdfGenerator'; // Necesitarás crear esta función
import ReminderFormModal from './ReminderFormModal';
import { getApiUrl } from '../utils/apiConfig';


function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const offset = d.getTimezoneOffset();
  d.setMinutes(d.getMinutes() - offset);
  return d.toISOString().slice(0, 10);
}
const Notifications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderToEdit, setReminderToEdit] = useState(null);

  const [members, setMembers] = useState([]);

  const [reminders, setReminders] = useState([]);

    // Cargar recordatorios desde el backend al montar el componente
const fetchReminders = () => {
  fetch(getApiUrl('/api/recordatorios'))
      .then(res => res.json())
      .then(data => setReminders(data));
  };

  const fetchMembers = () => {
  fetch(getApiUrl('/api/miembros'))
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]));
  };

  useEffect(() => {
    fetchReminders();
    fetchMembers();
  }, []);

  const getReminderTypeLabel = (type) => {
    const labels = {
      payment_due: 'Vencimiento de Pago',
      birthday: 'Cumpleaños'
    };
    return labels[type] || type;
  };

  const filteredReminders = reminders.filter(reminder => {
    const matchesSearch = reminder.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          reminder.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          reminder.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || reminder.reminderType === filterType;
    return matchesSearch && matchesFilter;
  });

  const upcomingRemindersCount = filteredReminders.filter(r => new Date(r.sendDate) >= new Date()).length;

  const handleExportPdf = () => {
    generateRemindersPdf(filteredReminders);
  };

  const handleAddReminder = () => {
    setReminderToEdit(null);
    setShowReminderModal(true);
  };

  const handleEditReminder = (reminder) => {
    setReminderToEdit(reminder);
    setShowReminderModal(true);
  };

  const handleDeleteReminder = (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este recordatorio?')) {
  fetch(getApiUrl(`/api/recordatorios/${id}`), {
        method: 'DELETE'
      })
        .then(res => res.json())
        .then(() => fetchReminders());
    }
  };

  const handleSaveReminder = (newReminderData) => {
    const member = members.find(m => m.id === parseInt(newReminderData.memberId));
    const reminderWithMemberName = { ...newReminderData, memberName: member ? member.name : 'Desconocido', memberId: member ? member.id : null };

  fetch(getApiUrl('/api/recordatorios'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reminderWithMemberName)
    })
      .then(res => res.json())
      .then(() => {
        setShowReminderModal(false);
        fetchReminders();
      });
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Configuración de Recordatorios
          </h1>
          <p className="text-gray-600">Gestione los recordatorios automáticos para sus socios</p>
        </div>
        
        <motion.button
          onClick={handleAddReminder}
          className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-5 h-5" />
          Crear Recordatorio
        </motion.button>
      </motion.div>

      {/* Reminder Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { 
            title: 'Total de Recordatorios', 
            value: reminders.length, 
            icon: Bell, 
            color: 'from-blue-500 to-cyan-500',
            bgColor: 'from-blue-50 to-cyan-50'
          },
          { 
            title: 'Recordatorios Próximos', 
            value: upcomingRemindersCount, 
            icon: Clock, 
            color: 'from-orange-500 to-amber-500',
            bgColor: 'from-orange-50 to-amber-50'
          }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              className={`bg-gradient-to-br ${stat.bgColor} border border-gray-200/50 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${stat.color} rounded-xl`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 font-medium">{stat.title}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Filters and Search */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por socio, asunto o mensaje..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
          </div>
          
          <div className="flex gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            >
              <option value="all">Todos los tipos</option>
              <option value="payment_due">Vencimiento de Pago</option>
              <option value="birthday">Cumpleaños</option>
            </select>
            
            <motion.button
              onClick={handleExportPdf}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Download className="w-5 h-5" />
              Exportar PDF
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Reminders Table */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Socio</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tipo</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fecha de Envío</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Asunto</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredReminders.map((reminder, index) => (
                <motion.tr
                  key={reminder.id}
                  className="hover:bg-gray-50/50 transition-colors duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.05 }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {reminder.memberName.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{reminder.memberName}</p>
                        <p className="text-sm text-gray-500">Socio ID: {reminder.memberId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
                      {getReminderTypeLabel(reminder.reminderType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(reminder.sendDate)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {reminder.subject}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <motion.button
                        onClick={() => handleEditReminder(reminder)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Edit className="w-4 h-4" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleDeleteReminder(reminder.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      <ReminderFormModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSave={handleSaveReminder}
        reminderToEdit={reminderToEdit}
        members={members}
      />
    </div>
  );
};

export default Notifications;